/**
 * AxLabs Experiments - Declarative Framework Generator
 *
 * Generates experiment classes from declarative schema objects.
 */
import { BaseSimulationEngine, DIMENSIONS, rk4StepNumeric, verletStep, vec2, } from '@axlabs/simulation-core';
// ============================================================================
// VALIDATION
// ============================================================================
/**
 * Validate an experiment schema for correctness and completeness.
 *
 * Checks for:
 * - Required fields present
 * - Dimensional consistency
 * - Valid variable references
 * - Parameter bounds sanity
 *
 * @param schema - Schema to validate
 * @returns Validation result with errors and warnings
 */
export function validateSchema(schema) {
    const errors = [];
    const warnings = [];
    // Check required metadata
    if (!schema.id || schema.id.trim() === '') {
        errors.push('Schema must have a non-empty id');
    }
    if (!schema.name || schema.name.trim() === '') {
        errors.push('Schema must have a non-empty name');
    }
    if (!['physics', 'chemistry', 'biology', 'mathematics'].includes(schema.domain)) {
        errors.push(`Invalid domain: ${schema.domain}. Must be 'physics', 'chemistry', 'biology', or 'mathematics'`);
    }
    // Check state variables
    if (!schema.stateVariables || schema.stateVariables.length === 0) {
        errors.push('Schema must have at least one state variable');
    }
    else {
        const stateIds = new Set();
        for (const sv of schema.stateVariables) {
            if (!sv.id || sv.id.trim() === '') {
                errors.push('State variable must have a non-empty id');
            }
            else if (stateIds.has(sv.id)) {
                errors.push(`Duplicate state variable id: ${sv.id}`);
            }
            else {
                stateIds.add(sv.id);
            }
            if (!sv.unit) {
                warnings.push(`State variable ${sv.id} is missing a unit`);
            }
        }
    }
    // Check parameters
    if (!schema.parameters || schema.parameters.length === 0) {
        errors.push('Schema must have at least one parameter');
    }
    else {
        const paramIds = new Set();
        for (const param of schema.parameters) {
            if (!param.id || param.id.trim() === '') {
                errors.push('Parameter must have a non-empty id');
            }
            else if (paramIds.has(param.id)) {
                errors.push(`Duplicate parameter id: ${param.id}`);
            }
            else {
                paramIds.add(param.id);
            }
            if (param.min >= param.max) {
                errors.push(`Parameter ${param.id}: min (${param.min}) must be less than max (${param.max})`);
            }
            if (param.default < param.min || param.default > param.max) {
                errors.push(`Parameter ${param.id}: default (${param.default}) must be within bounds [${param.min}, ${param.max}]`);
            }
            if (param.min < 0 && param.id.toLowerCase().includes('mass')) {
                warnings.push(`Parameter ${param.id} (mass) has negative minimum value`);
            }
        }
    }
    // Check derivatives function
    if (typeof schema.derivatives !== 'function') {
        errors.push('Schema must have a derivatives function');
    }
    // Check equations reference valid variables
    if (schema.equations) {
        for (const eq of schema.equations) {
            if (eq.variables) {
                for (const v of eq.variables) {
                    const validIds = new Set([
                        ...schema.stateVariables.map((sv) => sv.id),
                        ...schema.parameters.map((p) => p.id),
                    ]);
                    if (!validIds.has(v)) {
                        errors.push(`Equation ${eq.id} references unknown variable: ${v}`);
                    }
                }
            }
        }
    }
    // Check failure modes
    if (schema.failureModes) {
        const failureIds = new Set();
        for (const fm of schema.failureModes) {
            if (!fm.id || fm.id.trim() === '') {
                errors.push('Failure mode must have a non-empty id');
            }
            else if (failureIds.has(fm.id)) {
                errors.push(`Duplicate failure mode id: ${fm.id}`);
            }
            else {
                failureIds.add(fm.id);
            }
            if (typeof fm.check !== 'function') {
                errors.push(`Failure mode ${fm.id} must have a check function`);
            }
        }
    }
    // Check bounds reference valid state variables
    if (schema.bounds) {
        for (const [key, bound] of Object.entries(schema.bounds)) {
            if (!schema.stateVariables.find((sv) => sv.id === key)) {
                errors.push(`Bounds reference unknown state variable: ${key}`);
            }
            if (bound.min >= bound.max) {
                errors.push(`Invalid bounds for ${key}: min must be less than max`);
            }
        }
    }
    // Check stability variable exists
    if (schema.stabilityVariable) {
        if (!schema.stateVariables.find((sv) => sv.id === schema.stabilityVariable)) {
            errors.push(`Stability variable ${schema.stabilityVariable} not found in state variables`);
        }
    }
    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}
// ============================================================================
// CONVERSION HELPERS
// ============================================================================
/**
 * Convert schema parameter definitions to core ParameterDefinition format
 */
function convertParameters(schemaParams) {
    return schemaParams.map((p) => {
        const dimension = p.dimension ?? DIMENSIONS.DIMENSIONLESS;
        const range = p.max - p.min;
        const step = p.step ?? (range > 0 ? range / 100 : 0.01);
        const precision = p.precision ??
            (step < 0.001 ? 4 : step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0);
        return {
            id: p.id,
            name: p.name,
            symbol: p.symbol ?? p.id,
            unit: p.unit,
            dimension,
            min: p.min,
            max: p.max,
            default: p.default,
            step,
            description: p.description ?? '',
            precision,
            derived: false,
        };
    });
}
/**
 * Convert schema equation definitions to core EquationDefinition format
 */
function convertEquations(schemaEqs, allVariableIds) {
    if (!schemaEqs)
        return [];
    return schemaEqs.map((eq) => {
        // Auto-detect variables if not provided
        const variables = eq.variables ??
            allVariableIds.filter((v) => eq.latex.includes(v) || (eq.description && eq.description.includes(v)));
        if (eq.condition !== undefined) {
            return {
                id: eq.id,
                name: eq.name,
                latex: eq.latex,
                variables,
                description: eq.description ?? '',
                condition: eq.condition,
            };
        }
        return {
            id: eq.id,
            name: eq.name,
            latex: eq.latex,
            variables,
            description: eq.description ?? '',
        };
    });
}
/**
 * Convert schema failure modes to core FailureMode format
 */
function convertFailureModes(schemaFMs) {
    if (!schemaFMs)
        return [];
    return schemaFMs.map((fm) => ({
        id: fm.id,
        name: fm.name,
        description: fm.description ?? fm.name,
        check: fm.check,
        severity: fm.severity ?? 'warning',
        educationalMessage: fm.educationalMessage,
    }));
}
/**
 * Build initial conditions from state variable definitions
 */
function buildInitialConditions(schema) {
    if (schema.initialConditions && schema.initialConditions.length > 0) {
        return [...schema.initialConditions];
    }
    // Default: use initial value from each state variable definition
    return schema.stateVariables.map((sv) => ({
        variableId: sv.id,
        type: 'fixed',
        value: sv.initial,
    }));
}
/**
 * Ensure integration config has all required fields
 */
function ensureIntegrationConfig(integration) {
    return {
        type: integration?.type ?? 'rk4',
        dt: integration?.dt ?? 1 / 120,
        maxSubsteps: integration?.maxSubsteps ?? 10,
    };
}
// ============================================================================
// DETERMINISTIC RNG (Seeded Random)
// ============================================================================
/**
 * Seeded random number generator for reproducible simulations.
 *
 * Uses a Mulberry32 algorithm which is simple, fast, and has good
 * statistical properties for simulation purposes.
 */
export class SeededRandom {
    state;
    constructor(seed = 12345) {
        this.state = seed;
    }
    /**
     * Generate next random number in [0, 1)
     */
    next() {
        this.state |= 0;
        this.state = (this.state + 0x6d2b79f5) | 0;
        let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }
    /**
     * Generate random number in [min, max)
     */
    range(min, max) {
        return min + this.next() * (max - min);
    }
    /**
     * Generate random number from Gaussian distribution
     * Uses Box-Muller transform
     */
    gaussian(mean = 0, stddev = 1) {
        const u1 = this.next();
        const u2 = this.next();
        const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        return mean + z0 * stddev;
    }
    /**
     * Reset seed
     */
    reset(seed) {
        this.state = seed;
    }
}
// ============================================================================
// GENERATED SIMULATION CLASS
// ============================================================================
/**
 * Create a simulation class from an experiment schema.
 *
 * The generated class extends BaseSimulationEngine and implements
 * all required methods based on the schema configuration.
 *
 * @param schema - Experiment schema definition
 * @returns A class constructor that creates simulation instances
 *
 * @example
 * ```ts
 * const schema: ExperimentSchema = {
 *   id: 'my-experiment',
 *   name: 'My Experiment',
 *   domain: 'physics',
 *   description: 'A test experiment',
 *   stateVariables: [
 *     { id: 'x', name: 'Position', unit: 'm', initial: 0 },
 *     { id: 'v', name: 'Velocity', unit: 'm/s', initial: 1 },
 *   ],
 *   parameters: [
 *     { id: 'k', name: 'Constant', unit: 'N/m', min: 0.1, max: 10, default: 1 },
 *   ],
 *   derivatives: (state, params) => ({
 *     x: state.v,
 *     v: -params.k * state.x,
 *   }),
 * };
 *
 * const ExperimentClass = createExperimentFromSchema(schema);
 * const sim = new ExperimentClass();
 * ```
 */
export function createExperimentFromSchema(schema) {
    // Validate schema
    const validation = validateSchema(schema);
    if (!validation.valid) {
        throw new Error(`Invalid experiment schema:\n${validation.errors.join('\n')}`);
    }
    // Emit warnings
    if (validation.warnings.length > 0) {
        console.warn(`Experiment schema warnings for '${schema.id}':`);
        for (const warning of validation.warnings) {
            console.warn(`  - ${warning}`);
        }
    }
    // Precompute conversions
    const parameters = convertParameters(schema.parameters);
    const allVariableIds = [
        ...schema.stateVariables.map((sv) => sv.id),
        ...schema.parameters.map((p) => p.id),
    ];
    const equations = convertEquations(schema.equations, allVariableIds);
    const failureModes = convertFailureModes(schema.failureModes);
    const initialConditions = buildInitialConditions(schema);
    const rawIntegration = schema.integration;
    const integration = ensureIntegrationConfig(rawIntegration);
    // Convert difficulty to number if string
    let difficultyNum = 5;
    if (typeof schema.difficulty === 'number') {
        difficultyNum = schema.difficulty;
    }
    else if (schema.difficulty === 'beginner') {
        difficultyNum = 2;
    }
    else if (schema.difficulty === 'intermediate') {
        difficultyNum = 5;
    }
    else if (schema.difficulty === 'advanced') {
        difficultyNum = 8;
    }
    // Build experiment config
    const config = {
        id: schema.id,
        slug: schema.slug ?? schema.id,
        name: schema.name,
        domain: schema.domain,
        description: schema.description,
        longDescription: schema.longDescription ?? '',
        version: schema.version ?? 1,
        conceptTags: schema.conceptTags ?? [],
        difficulty: difficultyNum,
        prerequisites: schema.prerequisites ?? [],
        parameters,
        equations,
        initialConditions,
        failureModes,
        recommendedDt: integration.dt ?? 1 / 120,
        maxDt: (integration.dt ?? 1 / 120) * (integration.maxSubsteps ?? 10),
    };
    // State variable IDs for easy access
    const stateVariableIds = schema.stateVariables.map((sv) => sv.id);
    const initialStateValues = Object.fromEntries(schema.stateVariables.map((sv) => [sv.id, sv.initial]));
    // Create the simulation class
    class GeneratedSimulation extends BaseSimulationEngine {
        config = config;
        // Store schema functions for use in methods
        schemaDerivatives = schema.derivatives;
        schemaEnergyComputation = schema.energyComputation;
        schemaDerivedState = schema.derivedState;
        schemaInitialState = schema.initialState;
        schemaBounds = schema.bounds;
        schemaStabilityVariable = schema.stabilityVariable;
        noiseConfig = schema.noiseConfig;
        // Store state variable IDs for initialization
        stateIds = stateVariableIds;
        initialVals = initialStateValues;
        // Seeded RNG for noise and random elements
        seededRNG;
        constructor() {
            // Initialize with empty state, will be properly set in getInitialState
            super({}, integration, schema.historyLimit ?? 10000, schema.seed ?? 12345);
            this.seededRNG = new SeededRandom(schema.seed ?? 12345);
            // Set initial state values as defaults
            this.state = { ...this.initialVals };
        }
        getInitialState(params) {
            // Use custom initial state computation if provided
            if (this.schemaInitialState) {
                return { ...this.initialVals, ...this.schemaInitialState(params) };
            }
            // Otherwise, use initial conditions from config
            const state = { ...this.initialVals };
            for (const ic of initialConditions) {
                const vid = ic.variableId;
                switch (ic.type) {
                    case 'fixed':
                        state[vid] = ic.value ?? 0;
                        break;
                    case 'parameter':
                        const pid = ic.parameterId ?? vid;
                        state[vid] = params[pid] ?? 0;
                        break;
                    case 'random':
                        if (ic.random?.distribution === 'gaussian') {
                            const mean = ic.random.mean ?? 0;
                            const stddev = ic.random.stddev ?? 1;
                            state[vid] = this.seededRNG.gaussian(mean, stddev);
                        }
                        else {
                            const min = ic.random?.min ?? 0;
                            const max = ic.random?.max ?? 1;
                            state[vid] = this.seededRNG.range(min, max);
                        }
                        break;
                    case 'computed':
                        if (ic.compute) {
                            state[vid] = ic.compute(params);
                        }
                        break;
                }
            }
            return state;
        }
        computeDerivatives(state, params, t) {
            return this.schemaDerivatives(state, params, t);
        }
        integrate(dt) {
            const params = this.parameters;
            const state = this.state;
            const time = this.time;
            const integrationType = integration.type ?? 'rk4';
            if (integrationType === 'rk4') {
                // RK4 integration using rk4StepNumeric
                const derivative = (s, _t) => {
                    return { ...s, ...this.schemaDerivatives(s, params, _t) };
                };
                const newState = rk4StepNumeric(state, derivative, time, dt);
                this.state = newState;
            }
            else if (integrationType === 'verlet') {
                // Verlet integration (second-order systems)
                const firstId = this.stateIds[0];
                const secondId = this.stateIds[1];
                if (firstId !== undefined && secondId !== undefined) {
                    const getAcceleration = (pos) => {
                        const posVal = pos.x;
                        const tempState = { ...state, [firstId]: posVal };
                        const derivs = this.schemaDerivatives(tempState, params, time);
                        const acc = derivs[secondId] ?? 0;
                        return vec2(acc, 0);
                    };
                    const currentPosVal = state[firstId] ?? 0;
                    const currentPos = vec2(currentPosVal, 0);
                    // Estimate velocity from derivative
                    const derivs = this.schemaDerivatives(state, params, time);
                    const currentVelVal = derivs[firstId] ?? 0;
                    const currentVel = vec2(currentVelVal, 0);
                    const currentAcc = getAcceleration(currentPos);
                    const result = verletStep(currentPos, currentVel, currentAcc, getAcceleration, dt);
                    // Update state with Verlet result
                    const updatedState = { ...state };
                    updatedState[firstId] = result.position.x;
                    updatedState[secondId] = result.velocity.x;
                    this.state = updatedState;
                }
                else {
                    // Fallback to Euler if not enough state variables
                    const derivs = this.schemaDerivatives(state, params, time);
                    for (const key of this.stateIds) {
                        if (key === undefined)
                            continue;
                        const currentVal = this.state[key];
                        if (currentVal === undefined)
                            continue;
                        const deriv = derivs[key] ?? 0;
                        this.state[key] = currentVal + deriv * dt;
                    }
                }
            }
            else {
                // Euler integration (fallback)
                const derivs = this.schemaDerivatives(state, params, time);
                for (const key of this.stateIds) {
                    if (key === undefined)
                        continue;
                    const currentVal = this.state[key];
                    if (currentVal === undefined)
                        continue;
                    const deriv = derivs[key] ?? 0;
                    this.state[key] = currentVal + deriv * dt;
                }
            }
            // Apply derived state computation
            if (this.schemaDerivedState) {
                const derived = this.schemaDerivedState(this.state, params, time + dt);
                this.state = { ...this.state, ...derived };
            }
            // Apply noise if configured
            if (this.noiseConfig?.enabled) {
                this.applyNoise();
            }
        }
        computeEnergy(state, params) {
            if (this.schemaEnergyComputation) {
                return this.schemaEnergyComputation(state, params, this.time);
            }
            return undefined;
        }
        getBounds() {
            return this.schemaBounds;
        }
        getPositionForStabilityCheck() {
            if (this.schemaStabilityVariable) {
                return this.state[this.schemaStabilityVariable] ?? 0;
            }
            // Default: first state variable
            const firstKey = this.stateIds[0];
            return firstKey !== undefined ? this.state[firstKey] ?? 0 : 0;
        }
        /**
         * Apply configured noise to state variables
         */
        applyNoise() {
            const config = this.noiseConfig;
            if (!config?.enabled)
                return;
            const targets = config.targetVariables ?? this.stateIds;
            for (const key of targets) {
                const currentValue = this.state[key];
                if (currentValue === undefined)
                    continue;
                let noiseAmount = 0;
                if (config.type === 'gaussian') {
                    const sigma = config.sigma ?? 0.01;
                    noiseAmount = this.seededRNG.gaussian(0, Math.abs(currentValue) * sigma);
                }
                else {
                    const range = config.range ?? 0.01;
                    noiseAmount = this.seededRNG.range(-range, range) * Math.abs(currentValue);
                }
                this.state[key] = currentValue + noiseAmount;
            }
        }
        /**
         * Reset the RNG to its seed (for reproducibility)
         */
        resetRNG() {
            this.seededRNG.reset(schema.seed ?? 12345);
        }
    }
    return GeneratedSimulation;
}
// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================
/**
 * Create a simulation instance from a schema.
 *
 * Convenience wrapper around createExperimentFromSchema that
 * returns an instantiated simulation rather than a class.
 *
 * @param schema - Experiment schema definition
 * @returns A configured simulation instance
 *
 * @example
 * ```ts
 * const sim = createSimulationFromSchema(mySchema);
 * sim.initialize({ mass: 1, k: 10 });
 * const state = sim.step(0.016);
 * ```
 */
export function createSimulationFromSchema(schema) {
    const ExperimentClass = createExperimentFromSchema(schema);
    return new ExperimentClass();
}
/**
 * Create a simulation instance and initialize it in one call.
 *
 * @param schema - Experiment schema definition
 * @param params - Initial parameter values
 * @returns An initialized simulation instance
 *
 * @example
 * ```ts
 * const sim = createAndInitializeSimulation(mySchema, { mass: 1, k: 10 });
 * const state = sim.getState();
 * ```
 */
export function createAndInitializeSimulation(schema, params) {
    const sim = createSimulationFromSchema(schema);
    sim.initialize(params);
    return sim;
}
//# sourceMappingURL=generator.js.map